import React from 'react';
import { Layout } from '@/components/layout';
import { Card } from '@/components/ui/Card';

const AboutPage: React.FC = () => {
  return (
    <Layout currentPath='/about'>
      <div className='space-y-8'>
        {/* 页面标题 */}
        <div className='text-center'>
          <h1 className='text-3xl font-bold text-text-primary mb-2'>
            Elo & Glicko-2 算法说明
          </h1>
        </div>

        {/* Elo 算法简介 */}
        <Card className='p-8'>
          <h2 className='text-2xl font-semibold text-text-primary mb-6 flex items-center gap-2'>
            <span className='w-1 h-6 bg-glory-gold rounded'></span>
            什么是 Elo 算法？
          </h2>
          <div className='space-y-4 text-text-secondary leading-relaxed'>
            <p>
              Elo 等级分由匈牙利裔美国物理学家阿尔帕德·埃洛（Arpad Elo） 于 1960
              年提出，用以衡量对抗项目中选手的相对实力。
              它会在每场比赛后，把胜负的惊喜程度转化为分数增减，
              因而非常适合持续更新的排行榜。
            </p>
            <p>
              在纯 Elo 世界中，
              <span className='text-glory-gold font-medium'>
                最新分数就是实力的全部
              </span>
              。如果一位选手持续获胜，他们的分数会上升；
              如果输给明显更强的对手，分数下降得并不多。
              这种直观的设计帮助我们先建立基本概念，为理解 Glicko-2 奠定基础。
            </p>
          </div>
        </Card>

        {/* Elo 算法基本原理 */}
        <Card className='p-8'>
          <h2 className='text-2xl font-semibold text-text-primary mb-6 flex items-center gap-2'>
            <span className='w-1 h-6 bg-glory-gold rounded'></span>
            Elo 算法基本原理
          </h2>
          <div className='space-y-6'>
            {/* 胜率期望公式 */}
            <div>
              <h3 className='text-lg font-medium text-text-primary mb-3'>
                1. 胜率期望计算
              </h3>
              <div className='bg-background-primary p-4 rounded-lg border border-border mb-3'>
                <div className='text-glory-gold font-mono text-lg text-center'>
                  E<sub>A</sub> = 1 / (1 + 10
                  <sup>
                    ((R<sub>B</sub> - R<sub>A</sub>) / 400)
                  </sup>
                  )
                </div>
              </div>
              <p className='text-text-secondary'>
                E<sub>A</sub> 表示选手 A 的预期胜率； R<sub>A</sub>、R
                <sub>B</sub> 是双方当前分数。 当 A 比 B 强很多时，预期胜率接近
                1；实力接近时约为 0.5。
              </p>
            </div>

            {/* 等级分更新公式 */}
            <div>
              <h3 className='text-lg font-medium text-text-primary mb-3'>
                2. 等级分更新
              </h3>
              <div className='bg-background-primary p-4 rounded-lg border border-border mb-3'>
                <div className='text-glory-gold font-mono text-lg text-center'>
                  R'<sub>A</sub> = R<sub>A</sub> + K × (S<sub>A</sub> - E
                  <sub>A</sub>)
                </div>
              </div>
              <p className='text-text-secondary'>
                K 控制单场波动幅度，S<sub>A</sub>
                表示真实结果（胜=1，负=0）。打出爆冷时，(S - E) 的差值就大，
                因而会获得更多分数。
              </p>
            </div>
          </div>
        </Card>

        {/* 走向 Glicko-2 */}
        <Card className='p-8'>
          <h2 className='text-2xl font-semibold text-text-primary mb-6 flex items-center gap-2'>
            <span className='w-1 h-6 bg-glory-gold rounded'></span>
            进阶版 Elo 算法：Glicko-2
          </h2>
          <div className='space-y-4 text-text-secondary leading-relaxed'>
            <p>
              如果说传统的 Elo 评分是一把直尺，那么 Glicko-2
              就是一把能感知你状态的智能卷尺。 它由马克·格里克曼（Mark
              Glickman）开发，不仅告诉你分数，更关心两个问题：“我们对这个分数有多确定？”以及“这位选手的状态是稳定还是起伏？”
            </p>
            <ul className='list-disc list-inside space-y-2'>
              <li>
                <span className='text-text-primary font-medium'>
                  置信度（Rating Deviation, RD）
                </span>
                ：这就像选手分数旁的一个“问号”。长期没打比赛的选手，我们不确定他是进步了还是退步了，所以这个问号会变大（RD
                升高）。当他回归赛场并用战绩证明自己后，这个问号就会迅速缩小（RD
                降低）。
              </li>
              <li>
                <span className='text-text-primary font-medium'>
                  状态波动（Volatility）
                </span>
                ：该值用来判断一位选手是不是“神经刀”或“潜力股”。如果一位选手总能打出出人意料的比赛（比如爆冷战胜强敌），系统会认为他“波动性大”，并允许其分数像坐上火箭一样快速升降，以更快地找到其实际水平。
              </li>
            </ul>
          </div>
        </Card>

        {/* Glicko-2 核心机制 */}
        <Card className='p-8'>
          <h2 className='text-2xl font-semibold text-text-primary mb-6 flex items-center gap-2'>
            <span className='w-1 h-6 bg-glory-gold rounded'></span>
            Glicko-2 核心机制
          </h2>
          <div className='space-y-6'>
            <div>
              <h3 className='text-lg font-medium text-text-primary mb-3'>
                1. 选手核心数值
              </h3>
              <p className='text-text-secondary'>
                在 Glicko-2 系统里，每位选手都由三个数字定义：
                <code>(μ, φ, σ)</code>。<code>μ</code> 代表核心实力分，
                <code>φ</code> 是分数旁的“问号”大小（RD），<code>σ</code>{' '}
                则描述最近的状态是否像“过山车”（Volatility）。
              </p>
            </div>

            <div>
              <h3 className='text-lg font-medium text-text-primary mb-3'>
                2. 对阵胜率与权重
              </h3>
              <div className='bg-background-primary p-4 rounded-lg border border-border mb-3 space-y-2'>
                <div className='text-glory-gold font-mono text-lg text-center'>
                  g(φ) = 1 / √(1 + 3q²φ² / π²)
                </div>
                <div className='text-glory-gold font-mono text-lg text-center'>
                  E = 1 / (1 + e
                  <sup>
                    −g(φ<sub>对手</sub>) · (μ − μ<sub>对手</sub>)
                  </sup>
                  )
                </div>
              </div>
              <p className='text-text-secondary'>
                战胜一个“问号”很大的对手，和战胜一个发挥稳定的冠军，含金量显然不同。公式{' '}
                <code>g(φ)</code>{' '}
                就是一个“对手可信度”探测器，对手的“问号”越大，这场比赛的参考价值就越低。
                而 <code>E</code>{' '}
                则是赛前预测，它结合双方实力和对手的“可信度”，算出你的理论胜率。你实际的赛果（赢是1，输是0）和这个预测值的差距，就是评分更新的关键。
              </p>
            </div>

            <div>
              <h3 className='text-lg font-medium text-text-primary mb-3'>
                3. 赛后“复盘”与分数更新
              </h3>
              <div className='bg-background-primary p-4 rounded-lg border border-border mb-3 space-y-2'>
                <div className='text-glory-gold font-mono text-lg text-center'>
                  μ′ = μ + q ÷ (1 / φ² + 1 / v) × Σ g(φ<sub>j</sub>) · (s
                  <sub>j</sub> − E<sub>j</sub>)
                </div>
                <div className='text-glory-gold font-mono text-lg text-center'>
                  φ′ = √(1 / (1 / φ² + 1 / v))
                </div>
              </div>
              <p className='text-text-secondary'>
                比赛结束后，系统开始结算。公式的核心是 <code>(s_j − E_j)</code>
                ，即“实际赛果”与“赛前预测”的差距。系统会把你近期所有比赛的“意外程度”汇总起来，并根据每场比赛的“含金量”加权，算出你的新实力分{' '}
                <code>μ′</code>。 同时，<code>φ′</code>{' '}
                也会更新。如果你的战绩都在预料之中，说明你的水平很稳定，“问号”就会变小。
                至于 <code>σ′</code> 和 <code>v</code>
                ，可以理解为系统的“微调”工具，确保系统在面对大量爆冷赛果时，既能快速反应，又不会变得混乱。
              </p>
            </div>

            <div>
              <h3 className='text-lg font-medium text-text-primary mb-3'>
                4. 前端展示分数
              </h3>
              <div className='bg-background-primary p-4 rounded-lg border border-border mb-3'>
                <div className='text-glory-gold font-mono text-lg text-center'>
                  Elo = μ - 2 * φ
                </div>
              </div>
              <p className='text-text-secondary'>
                我们在前端展示的 Elo 分数采用 <code>μ - 2 * φ</code>{' '}
                的保守估计：
                不确定性高的选手会被轻微下调，稳定发挥的选手则能更稳地站在高分段。
              </p>
            </div>
          </div>
        </Card>

        {/* 项目实践 */}
        <Card className='p-8'>
          <h2 className='text-2xl font-semibold text-text-primary mb-6 flex items-center gap-2'>
            <span className='w-1 h-6 bg-glory-gold rounded'></span>
            Glicko-2 评分流程
          </h2>
          <div className='space-y-4 text-text-secondary leading-relaxed'>
            <p>
              收集每场完整的 5v5 对局，使用团队版 Glicko-2
              算法先在队伍层面估计胜率，再把变化分配给个人。
            </p>
            <ul className='list-disc list-inside space-y-2'>
              <li>
                <span className='text-text-primary font-medium'>初始状态</span>
                ：新选手以 μ = 2600、φ = 350 开始，保留足够的探索空间。
              </li>
              <li>
                <span className='text-text-primary font-medium'>
                  赛事等级权重
                </span>
                ：S 级 ×1.25，A 级 ×1.0，B 级 ×0.75，C 级 ×0.5，D 级 ×0.25。
                赛事越顶级，评分改动越显著。
              </li>
              <li>
                <span className='text-text-primary font-medium'>
                  赛程阶段加成
                </span>
                ：常规赛 ×1.0，季后赛 ×1.5，总决赛
                ×2.0，让关键战役的影响更突出。
              </li>
              <li>
                <span className='text-text-primary font-medium'>团队分摊</span>
                ：先构建虚拟“队伍选手”完成对阵，再依据每位选手的 RD
                与表现权重拆分增减，做到既公平又兼顾个人差异。
              </li>
              <li>
                <span className='text-text-primary font-medium'>
                  胜场 MVP 奖励
                </span>
                ：胜方评价最高的选手获得约 10%
                的额外增益，其余队友降低少量涨幅，避免功劳被独占或被忽略。
              </li>
              <li>
                <span className='text-text-primary font-medium'>保守展示</span>
                ：排行榜使用 μ - 2 * φ
                的保守分数，既能突出强者，也会提醒我们哪些选手仍在验证实力。
              </li>
              <li>
                <span className='text-text-primary font-medium'>
                  不活跃惩罚
                </span>
                ：超 90 天未出战的选手，φ
                会自动膨胀，降低我们对旧成绩的信任；重返赛场后，几场高质量对局即可迅速收敛。
              </li>
            </ul>
          </div>
        </Card>

        {/* Elo 分数含义 */}
        <Card className='p-8'>
          <h2 className='text-2xl font-semibold text-text-primary mb-6 flex items-center gap-2'>
            <span className='w-1 h-6 bg-glory-gold rounded'></span>
            Elo 分数等级划分
          </h2>
          <div className='space-y-4 text-text-secondary'>
            <p>结合历史数据，我们将展示分数划分为以下等级：</p>
            <ul className='list-disc list-inside space-y-2'>
              <li>2800 以上：S+（殿堂级）</li>
              <li>2700 - 2800：S（传奇级）</li>
              <li>2600 - 2700：A+（顶尖级）</li>
              <li>2400 - 2600：A（职业级）</li>
              <li>2200 - 2400：B（准职业级）</li>
              <li>2000 - 2200：C（新锐级）</li>
              <li>2000 以下：D（基础级）</li>
            </ul>
          </div>
        </Card>

        {/* 数据来源说明 */}
        <Card className='p-8'>
          <h2 className='text-2xl font-semibold text-text-primary mb-6 flex items-center gap-2'>
            <span className='w-1 h-6 bg-glory-gold rounded'></span>
            数据来源说明
          </h2>
          <div className='space-y-4 text-text-secondary leading-relaxed'>
            <p>
              所有比赛数据来自公开的职业比赛对局记录，手动或自动清洗、对齐别名、映射战队历史，再进入
              Glicko-2 评分流程。
            </p>
            <p className='text-sm text-text-muted mt-6'>
              注：本网站的排名仅供学习与交流，不代表官方排名。
            </p>
          </div>
        </Card>
      </div>
    </Layout>
  );
};

export default AboutPage;
